import React, { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import PostForm from "../components/PostForm";
import { createPost, deletePost, getPost, updatePost } from "../utils/apiClient";
import useDocumentTitle from "../hooks/useDocumentTitle";

function PostEditor() {
  const { postUuid } = useParams();
  useDocumentTitle(postUuid ? "Rediger indlæg" : "Nyt indlæg");
  const navigate = useNavigate();
  const isEdit = Boolean(postUuid);
  const [postRef, setPostRef] = useState(postUuid || "");

  const [initial, setInitial] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isEdit) {
      setInitial({ title: "", body: "", visibility: "private", composition: "journal", tags: "" });
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const post = await getPost(postUuid);
        if (cancelled) return;
        setPostRef(post.permalink || post.post_uuid);
        setInitial({
          title: post.title || "",
          body: post.body || "",
          photos: post.photos || [],
          visibility: post.visibility || "private",
          composition: post.composition || "journal",
          tags: post.tags || [],
        });
      } catch (err) {
        if (!cancelled) {
          const detail = err.response?.data?.detail;
          setError(typeof detail === "string" ? detail : err.message || "Failed to load post");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, postUuid]);

  const handleSubmit = async (payload) => {
    if (isEdit) {
      await updatePost(postRef || postUuid, payload);
      navigate("/profile?tab=posts");
    } else {
      const created = await createPost(payload);
      navigate(`/admin/posts/${created.post_uuid}/edit`);
    }
  };

  const handleDelete = async () => {
    await deletePost(postRef || postUuid);
    navigate("/profile?tab=posts");
  };

  if (loading || !initial) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{ py: { xs: 4, sm: 6 } }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, letterSpacing: "-0.02em" }}>
          {isEdit ? "Edit post" : "New post"}
        </Typography>
        <Stack direction="row" spacing={1}>
          {isEdit ? (
            <Button
              component={RouterLink}
              to={`/posts/${postRef || postUuid}`}
              variant="outlined"
            >
              View
            </Button>
          ) : null}
          <Button component={RouterLink} to="/profile?tab=posts" variant="text">
            Back to list
          </Button>
        </Stack>
      </Stack>

      {error ? (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      ) : null}

      <PostForm
        key={isEdit ? postUuid : "new"}
        initial={initial}
        onSubmit={handleSubmit}
        onDelete={isEdit ? handleDelete : undefined}
        submitLabel="Save"
        postUuid={isEdit ? postUuid : undefined}
      />
    </Box>
  );
}

export default PostEditor;
